import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";

import { Button, Icon, useAlert } from "@web-app/components";
import Navigation from "@web-app/pages/common/Navigation";
import {
  addChart,
  fetchDashboard,
  updateChartPositions,
} from "@web-app/services/analytics";
import type { Chart, CreateChartPayload, Dashboard } from "@web-app/types";

import DashboardGrid from "./DashboardGrid";
import DateRangeDropdown from "./DateRangeDropdown";
import ChartBuilder from "./components/ChartBuilder";
import { dateOnlyToLocalDate } from "./dateUtils";
import { exportDashboardToPdf } from "./utils/exportPdf";

type DashboardPageProps = {
  dashboardId: string;
  logout: () => void;
};

export default function DashboardPage({
  dashboardId,
  logout,
}: DashboardPageProps) {
  const { showAlert } = useAlert();
  const gridRef = useRef<HTMLDivElement>(null);
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [charts, setCharts] = useState<Chart[]>([]);
  const [tempCharts, setTempCharts] = useState<Chart[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isChartBuilderOpen, setIsChartBuilderOpen] = useState(false);
  const [editingChart, setEditingChart] = useState<Chart | null>(null);

  // Date range state
  const [dateRange, setDateRange] = useState({
    from: dayjs().subtract(7, "day").format("YYYY-MM-DD"),
    to: dayjs().format("YYYY-MM-DD"),
  });

  // Load dashboard on mount
  useEffect(() => {
    loadDashboard();
  }, [dashboardId]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchDashboard(dashboardId);
      setDashboard(data);
      setCharts(data.charts || []);
      setTempCharts(data.charts || []);
    } catch (err) {
      console.error("Failed to load dashboard:", err);
      setError("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleEditDashboard = () => {
    setIsEditMode(true);
    setTempCharts([...charts]);
  };

  const handleSaveChanges = async () => {
    try {
      // Extract only position updates
      const positions = tempCharts.map((chart) => ({
        gridPosition: chart.gridPosition,
        id: chart.id,
      }));

      // Save positions to backend
      await updateChartPositions(dashboardId, { positions });

      setCharts(tempCharts);
      setIsEditMode(false);
    } catch (err) {
      console.error("Failed to save chart positions:", err);
      showAlert({
        message: "Failed to save changes. Please try again.",
        title: "Error",
        type: "error",
      });
    }
  };

  const handleCancelEdit = () => {
    setTempCharts(charts);
    setIsEditMode(false);
  };

  const handleAddChart = () => {
    setEditingChart(null);
    setIsChartBuilderOpen(true);
  };

  const handleEditChart = (chart: Chart) => {
    setEditingChart(chart);
    setIsChartBuilderOpen(true);
  };

  const handleDuplicateChart = async (chart: Chart) => {
    try {
      // Place the duplicate at the bottom of the existing charts
      const maxY = charts.reduce((max, c) => {
        const bottom = c.gridPosition.y + c.gridPosition.h;
        return Math.max(max, bottom);
      }, 0);

      const payload: CreateChartPayload = {
        config: chart.config,
        cubeQuery: chart.cubeQuery,
        gridPosition: {
          h: chart.gridPosition.h,
          w: chart.gridPosition.w,
          x: 0,
          y: maxY,
        },
        queryType: chart.queryType,
        sqlQuery: chart.sqlQuery,
        title: `${chart.title} Duplicate`,
        type: chart.type,
      };

      await addChart(dashboardId, payload);
      loadDashboard();
    } catch (err) {
      console.error("Failed to duplicate chart:", err);
      showAlert({
        message: "Failed to duplicate chart. Please try again.",
        title: "Error",
        type: "error",
      });
    }
  };

  const handleChartCreated = () => {
    // Reload dashboard to get the new/updated chart
    loadDashboard();
    setEditingChart(null);
  };

  const handleCloseChartBuilder = () => {
    setIsChartBuilderOpen(false);
    setEditingChart(null);
  };

  const handleExportPdf = async () => {
    if (!gridRef.current || !dashboard) return;

    setIsExporting(true);
    try {
      await exportDashboardToPdf({
        charts,
        dashboardTitle: dashboard.title,
        dateRange,
        gridElement: gridRef.current,
      });
    } catch (err) {
      console.error("Failed to export PDF:", err);
      showAlert({
        message: "Failed to export PDF. Please try again.",
        title: "Export Error",
        type: "error",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const updateChartLayout = (
    chartId: string | null,
    newPosition: { h: number; w: number; x: number; y: number } | null,
    fullLayout?: Chart[],
  ) => {
    if (fullLayout) {
      // Update entire layout (for auto-adjust)
      setTempCharts(fullLayout);
    } else if (chartId && newPosition) {
      // Update single chart
      setTempCharts((prevCharts) =>
        prevCharts.map((chart) =>
          chart.id === chartId
            ? { ...chart, gridPosition: newPosition }
            : chart,
        ),
      );
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex h-screen">
        <Navigation logout={logout} />
        <div className="flex-1 lg:pl-20 flex items-center justify-center">
          <div className="text-sm text-gray-500">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !dashboard) {
    return (
      <div className="flex h-screen">
        <Navigation logout={logout} />
        <div className="flex-1 lg:pl-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {error || "Dashboard not found"}
            </h1>
            <p className="text-gray-500 mb-4">
              The dashboard you're looking for doesn't exist or failed to load.
            </p>
            <a
              href="/analytics"
              className="inline-block text-blue-600 hover:text-blue-700"
            >
              Back to Analytics
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen">
      <Navigation logout={logout} />

      <div className="flex-1 lg:pl-20 overflow-y-auto">
        <div className="">
          <div className="sticky top-0 z-20 bg-white">
            <div>
              <div className="flex items-center gap-2 px-6 pt-4">
                <a
                  href="/analytics"
                  className="mr-2 h-6 text-gray-700 hover:text-gray-900 transition-colors"
                >
                  <Icon name="fasChevronLeft" className="h-4 w-4" />
                </a>
                <div>
                  <h1 className="font-semibold text-xl text-gray-700">
                    {dashboard.title}
                  </h1>
                  <p className="text-sm text-gray-400">
                    {dashboard.description}
                  </p>
                </div>
              </div>
              <hr className="w-full mt-5 mb-3 border-gray-200" />
            </div>
          </div>

          <div className="">
            {/* Dashboard Navigation */}
            <div className="flex justify-between items-center border-b border-gray-200 pb-3 px-6">
              <div className="flex items-center gap-3">
                <DateRangeDropdown
                  dateRange={{
                    from: dateOnlyToLocalDate(dateRange.from),
                    to: dateOnlyToLocalDate(dateRange.to),
                  }}
                  onChange={(range) => {
                    setDateRange({
                      from: dayjs(range.from).format("YYYY-MM-DD"),
                      to: dayjs(range.to).format("YYYY-MM-DD"),
                    });
                  }}
                />
              </div>
              <div className="flex gap-3 items-center">
                {isEditMode ? (
                  <>
                    <Button plain onClick={handleCancelEdit}>
                      Cancel
                    </Button>
                    <Button onClick={handleSaveChanges}>Save</Button>
                    <Button icon="plus" onClick={handleAddChart}>
                      Add Chart
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      icon="fasFileExport"
                      plain
                      onClick={handleExportPdf}
                      disabled={isExporting || charts.length === 0}
                      isLoading={isExporting}
                    >
                      {isExporting ? "Exporting..." : "Export PDF"}
                    </Button>
                    <Button
                      icon="fasPencil"
                      plain
                      onClick={handleEditDashboard}
                    >
                      Edit
                    </Button>
                  </>
                )}
              </div>
            </div>

            <div>
              {charts.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-12 mx-6 mt-6 rounded-lg border bg-gray-50 border-gray-200">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                    <Icon name="chartMixedLight" className="text-gray-400" />
                  </div>
                  <h3 className="mt-4 text-sm font-medium text-gray-900">
                    No charts yet
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 text-center">
                    Add your first chart to start visualizing your data
                  </p>
                  <Button
                    type="button"
                    color="light"
                    onClick={handleAddChart}
                    className="mt-4"
                  >
                    Add Chart
                  </Button>
                </div>
              ) : (
                <DashboardGrid
                  ref={gridRef}
                  charts={isEditMode ? tempCharts : charts}
                  dateRange={dateRange}
                  isEditMode={isEditMode}
                  onEditChart={handleEditChart}
                  onDuplicateChart={handleDuplicateChart}
                  onLayoutChange={updateChartLayout}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Chart Builder Drawer */}
      <ChartBuilder
        key={editingChart?.id || "new"}
        chart={editingChart || undefined}
        charts={charts}
        isOpen={isChartBuilderOpen}
        onClose={handleCloseChartBuilder}
        dashboardId={dashboardId}
        dashboardDateRange={dateRange}
        onChartCreated={handleChartCreated}
      />
    </div>
  );
}
